/**
 * Pure drawing layer for the share-card export.
 *
 * The user-provided `/share-card-template.png` is drawn full-bleed as
 * the background — no corner glows, no programmatic tint, no second-
 * guessing. The only overlay is a soft radial darken behind the
 * headline so the big `$X.XX` reads cleanly over whatever artwork is
 * in the template; edges, corners, and the brand strip area are
 * left untouched.
 *
 * The same `<canvas>` is shown live in the preview pane and exported
 * via `canvas.toBlob` — preview and export are pixel-identical by
 * construction.
 */

import { fmtMoney } from "@/lib/format";

export const CARD_W = 1254;
export const CARD_H = 1254;

export interface ShareTrade {
  status: "Open Trade" | "Closed Trade";
  /** Evaluation: no real payout (challenge progress only). Funded:
   *  the displayed PnL × payoutPct% is what the trader actually keeps. */
  accountKind: "evaluation" | "funded";
  /** Profit split percentage (0–100). Ignored for evaluation accounts. */
  payoutPct: number;
  trader: string;
  symbol: string;
  side: "buy" | "sell";
  lots: number;
  /** Per-leg P/L in USD. The card never sums hedge legs — this is the
   *  trade the user picked to share. */
  profit: number;
  openPrice: number;
  closePrice: number | null;
  openedAt: string;
  closedAt: string;
}

export interface RenderAssets {
  /** User-provided background template (full-bleed candle/brand artwork). */
  bg: HTMLImageElement;
  logo: HTMLImageElement;
  qr: HTMLImageElement;
  /** CSS-resolved --accent value (oklch / rgb / hex — passed straight to fillStyle). */
  accent: string;
}

const FONT_FAMILY = "Geist, system-ui, -apple-system, Arial, sans-serif";

/** Every weight × size combo we draw with — preloaded so the export
 *  never falls back to system fonts. */
const FONT_LOAD: Array<readonly [string, number]> = [
  ["400", 18],
  ["400", 20],
  ["400", 22],
  ["400", 38],
  ["500", 24],
  ["500", 36],
  ["700", 18],
  ["700", 20],
  ["700", 25],
  ["700", 28],
  ["800", 28],
  ["800", 32],
  ["800", 124],
];

export async function loadGeistFonts(): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) return;
  await Promise.all(
    FONT_LOAD.map(([w, sz]) => document.fonts.load(`${w} ${sz}px Geist`)),
  );
}

export function getAccentFromCss(): string {
  if (typeof window === "undefined") return "oklch(0.718 0.202 349.761)";
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue("--accent")
    .trim();
  return v || "oklch(0.718 0.202 349.761)";
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function font(weight: string, size: number): string {
  return `${weight} ${size}px ${FONT_FAMILY}`;
}

function inputToDisplay(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

interface PillSpec {
  text: string;
  fill: CanvasGradient | string;
  textColor: string;
  border: string;
}

function measurePill(
  ctx: CanvasRenderingContext2D,
  text: string,
  padX: number,
): number {
  return Math.ceil(ctx.measureText(text).width) + padX * 2;
}

function drawBackground(
  ctx: CanvasRenderingContext2D,
  bg: HTMLImageElement,
): void {
  // User-provided template fills the canvas at full bleed.
  ctx.drawImage(bg, 0, 0, CARD_W, CARD_H);

  // Soft centerward darken so the big headline always reads cleanly
  // over whatever artwork is in the template. Edges stay untouched.
  const dark = ctx.createRadialGradient(
    CARD_W / 2,
    CARD_H * 0.45,
    CARD_W * 0.12,
    CARD_W / 2,
    CARD_H * 0.45,
    CARD_W * 0.55,
  );
  dark.addColorStop(0, "rgba(7, 4, 18, 0.42)");
  dark.addColorStop(1, "rgba(7, 4, 18, 0)");
  ctx.fillStyle = dark;
  ctx.fillRect(0, 0, CARD_W, CARD_H);
}

function drawLogo(
  ctx: CanvasRenderingContext2D,
  logo: HTMLImageElement,
  x: number,
  y: number,
  size: number,
): void {
  ctx.save();
  // Soft rounded mask for the logo so it reads as a brand mark, not a square sticker.
  roundedRect(ctx, x, y, size, size, size * 0.26);
  ctx.clip();
  ctx.drawImage(logo, x, y, size, size);
  ctx.restore();
  // Subtle inset border.
  roundedRect(ctx, x + 0.5, y + 0.5, size - 1, size - 1, size * 0.26);
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 1;
  ctx.stroke();
}

export function renderTradeCard(
  canvas: HTMLCanvasElement,
  trade: ShareTrade,
  assets: RenderAssets,
): void {
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  drawBackground(ctx, assets.bg);

  // ============================================================
  // Top section: logo + status pill + phase pill + trader name
  // ============================================================
  drawLogo(ctx, assets.logo, 64, 64, 96);

  const statusW = 440;
  const statusH = 60;
  const statusX = (CARD_W - statusW) / 2;
  const statusY = 84;
  const statusGrad = ctx.createLinearGradient(
    statusX,
    statusY,
    statusX + statusW,
    statusY + statusH,
  );
  statusGrad.addColorStop(0, "rgba(25, 10, 42, 0.84)");
  statusGrad.addColorStop(1, "rgba(255, 76, 175, 0.20)");
  roundedRect(ctx, statusX, statusY, statusW, statusH, statusH / 2);
  ctx.fillStyle = statusGrad;
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth = 1.25;
  ctx.stroke();
  ctx.fillStyle = "rgba(255,255,255,0.96)";
  ctx.font = font("700", 26);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(trade.status, CARD_W / 2, statusY + statusH / 2 + 1);

  // Phase pill — smaller, sits below status pill
  const phaseLabel =
    trade.accountKind === "funded"
      ? `Funded · ${Math.round(trade.payoutPct)}% Split`
      : "Evaluation Account";
  ctx.font = font("700", 18);
  const phaseTextW = Math.ceil(ctx.measureText(phaseLabel).width);
  const phasePadX = 18;
  const phaseW = phaseTextW + phasePadX * 2;
  const phaseH = 36;
  const phaseX = (CARD_W - phaseW) / 2;
  const phaseY = statusY + statusH + 12;

  const phaseFill =
    trade.accountKind === "funded"
      ? "rgba(18, 56, 36, 0.86)"
      : "rgba(70, 48, 14, 0.86)";
  const phaseStroke =
    trade.accountKind === "funded"
      ? "rgba(110, 220, 160, 0.55)"
      : "rgba(240, 180, 90, 0.55)";
  roundedRect(ctx, phaseX, phaseY, phaseW, phaseH, phaseH / 2);
  ctx.fillStyle = phaseFill;
  ctx.fill();
  ctx.strokeStyle = phaseStroke;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = "rgba(255,255,255,0.96)";
  ctx.font = font("700", 18);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(phaseLabel, CARD_W / 2, phaseY + phaseH / 2 + 1);

  // Trader name (top-right)
  ctx.fillStyle = "rgba(255,255,255,0.96)";
  ctx.font = font("700", 26);
  ctx.textAlign = "right";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(trade.trader.toUpperCase().slice(0, 28), 1190, 116);

  // ============================================================
  // Center stack — profit headline lifted up from absolute center
  // for cleaner balance with the bottom card below.
  //   - PROFIT/LOSS label  cy=440
  //   - Big number         cy=545
  //   - Take-home note     cy=648
  //   - Pills row          cy=730
  // ============================================================
  const positive = trade.profit >= 0;
  const headlineColor = positive
    ? "rgba(255,255,255,0.98)"
    : "rgba(255,150,180,0.98)";
  const labelColor = positive
    ? "rgba(255,255,255,0.86)"
    : "rgba(255,170,190,0.92)";

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  ctx.fillStyle = labelColor;
  ctx.font = font("500", 36);
  ctx.fillText(positive ? "PROFIT" : "LOSS", CARD_W / 2, 440);

  ctx.fillStyle = headlineColor;
  ctx.font = font("800", 124);
  ctx.fillText(fmtMoney(trade.profit), CARD_W / 2, 545);

  // Take-home note. Always shown so viewers know whether the dollar
  // figure on the headline translates to real money.
  let payoutNote: string;
  if (trade.accountKind === "evaluation") {
    payoutNote = "Evaluation Trade · Challenge Progress · No Payout";
  } else if (positive) {
    const takeHome = trade.profit * (trade.payoutPct / 100);
    payoutNote = `Take-Home Payout: ${fmtMoney(takeHome)} · ${Math.round(trade.payoutPct)}% Split`;
  } else {
    payoutNote = `Funded Account · ${Math.round(trade.payoutPct)}% Split`;
  }
  ctx.fillStyle = "rgba(255,255,255,0.66)";
  ctx.font = font("500", 24);
  ctx.fillText(payoutNote, CARD_W / 2, 648);

  // Pills row (side / lots / symbol), centered
  const pillH = 50;
  const pillCY = 730;
  const pillY = pillCY - pillH / 2;
  const pillRadius = pillH / 2;
  const pillGap = 18;
  const sidePadX = 24;
  const lotsPadX = 22;
  const symbolPadX = 22;

  ctx.font = font("700", 25);
  const sideText = trade.side === "sell" ? "Sell" : "Buy";
  const sideW = measurePill(ctx, sideText, sidePadX);
  const lotsText = `${trade.lots} lots`;
  const lotsW = measurePill(ctx, lotsText, lotsPadX);
  const symbolText = trade.symbol.toUpperCase();
  const symbolW = measurePill(ctx, symbolText, symbolPadX);

  const totalPills = sideW + pillGap + lotsW + pillGap + symbolW;
  const pillStart = (CARD_W - totalPills) / 2;

  const pills: Array<PillSpec & { x: number; w: number }> = [
    {
      x: pillStart,
      w: sideW,
      text: sideText,
      fill: trade.side === "sell" ? "rgba(158, 28, 76, 0.96)" : assets.accent,
      textColor: "rgba(255,255,255,0.98)",
      border: "rgba(255,255,255,0.22)",
    },
    {
      x: pillStart + sideW + pillGap,
      w: lotsW,
      text: lotsText,
      fill: "rgba(40, 28, 80, 0.92)",
      textColor: "rgba(255,255,255,0.96)",
      border: "rgba(255,255,255,0.18)",
    },
    {
      x: pillStart + sideW + pillGap + lotsW + pillGap,
      w: symbolW,
      text: symbolText,
      fill: "rgba(48, 28, 92, 0.92)",
      textColor: "rgba(255,255,255,0.96)",
      border: "rgba(255,255,255,0.18)",
    },
  ];

  ctx.font = font("700", 25);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (const p of pills) {
    roundedRect(ctx, p.x, pillY, p.w, pillH, pillRadius);
    ctx.fillStyle = p.fill;
    ctx.fill();
    ctx.strokeStyle = p.border;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = p.textColor;
    ctx.fillText(p.text, p.x + p.w / 2, pillY + pillH / 2 + 1);
  }

  // ============================================================
  // Bottom card — open/close info on top, brand+QR on bottom.
  // y=860..1230 (h=370). Tighter typography than v1.
  // ============================================================
  const stripX = 40;
  const stripY = 860;
  const stripW = CARD_W - stripX * 2;
  const stripH = 370;
  const stripRight = stripX + stripW;
  const stripPadX = 56;

  roundedRect(ctx, stripX, stripY, stripW, stripH, 30);
  ctx.fillStyle = "rgba(7, 4, 18, 0.62)";
  ctx.fill();
  ctx.strokeStyle = "rgba(255, 76, 175, 0.32)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // ---- Open / close info (top half of the card) ----
  const closeLabel =
    trade.status === "Closed Trade" ? "Close Price" : "Live Price";
  const closeValue = trade.closePrice ?? trade.openPrice;
  const infoLabelY = 906;
  const infoValueY = 950;
  const infoTimeY = 982;

  ctx.textBaseline = "alphabetic";

  // Left block (open)
  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(255,255,255,0.62)";
  ctx.font = font("400", 20);
  ctx.fillText("Open Price", stripX + stripPadX, infoLabelY);
  ctx.fillStyle = "rgba(255,255,255,0.98)";
  ctx.font = font("800", 32);
  ctx.fillText(`$${trade.openPrice.toFixed(2)}`, stripX + stripPadX, infoValueY);
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = font("400", 18);
  ctx.fillText(inputToDisplay(trade.openedAt), stripX + stripPadX, infoTimeY);

  // Right block (close / live)
  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(255,255,255,0.62)";
  ctx.font = font("400", 20);
  ctx.fillText(closeLabel, stripRight - stripPadX, infoLabelY);
  ctx.fillStyle = "rgba(255,255,255,0.98)";
  ctx.font = font("800", 32);
  ctx.fillText(`$${closeValue.toFixed(2)}`, stripRight - stripPadX, infoValueY);
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = font("400", 18);
  ctx.fillText(
    inputToDisplay(trade.closedAt || trade.openedAt),
    stripRight - stripPadX,
    infoTimeY,
  );

  // Center arrow between values
  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.font = font("400", 38);
  ctx.fillText("→", CARD_W / 2, infoValueY);

  // Subtle divider between trade info and brand
  ctx.beginPath();
  ctx.moveTo(stripX + stripPadX, 1018);
  ctx.lineTo(stripRight - stripPadX, 1018);
  ctx.strokeStyle = "rgba(255,255,255,0.10)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // ---- Brand + QR (bottom half of the card) ----
  // Wordmark + tagline + QR shift down together so the brand sits in
  // the lower half of the card instead of hugging the divider.
  const brandLabelY = 1116;
  const brandTaglineY = 1156;
  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(255,255,255,0.98)";
  ctx.font = font("800", 28);
  ctx.fillText("pfh.trading", stripX + stripPadX, brandLabelY);
  ctx.fillStyle = "rgba(255,255,255,0.62)";
  ctx.font = font("400", 20);
  ctx.fillText(
    "Hunt every prop firm discount in one place.",
    stripX + stripPadX,
    brandTaglineY,
  );

  // QR tile — moved down with the wordmark/tagline so the whole brand
  // band reads as one block. Centered around the same vertical line
  // as the tagline midpoint.
  const qrSize = 100;
  const qrX = stripRight - qrSize - 28;
  const qrY = 1078 + (160 - qrSize) / 2;
  roundedRect(ctx, qrX, qrY, qrSize, qrSize, 12);
  ctx.fillStyle = "rgba(255,255,255,0.98)";
  ctx.fill();
  const qrMargin = 8;
  ctx.drawImage(
    assets.qr,
    qrX + qrMargin,
    qrY + qrMargin,
    qrSize - qrMargin * 2,
    qrSize - qrMargin * 2,
  );
}
